import { DOCUMENT, isPlatformServer } from '@angular/common';
import * as i0 from '@angular/core';
import { DestroyRef, inject, ElementRef, PLATFORM_ID, Component, ChangeDetectionStrategy, Input, ViewChild } from '@angular/core';
import { debounceTime, fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

class MarqueeComponent {
    constructor() {
        this.speed = 1;
        this.fill = true;
        this.enlarge = false;
        this.clonedElements = [];
        this.destroyRef = inject(DestroyRef);
        this.hostElement = inject(ElementRef);
        this.document = inject(DOCUMENT);
        this.platformId = inject(PLATFORM_ID);
        this.boundPointerMove = this.onPointerMove.bind(this);
    }
    ngAfterViewInit() {
        this.browserSetup();
    }
    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.unobserve(this.contentElement.nativeElement);
        }
    }
    onContentResize() {
        for (const clonedElement of this.clonedElements) {
            clonedElement.remove();
        }
        const hostWidth = this.hostElement.nativeElement.clientWidth;
        if (this.fill) {
            const contentWidth = this.contentElement.nativeElement.clientWidth;
            if (contentWidth > 0) {
                const contentCopies = Math.ceil(hostWidth / contentWidth) - 1;
                for (let i = 0; i < contentCopies; i++) {
                    // Fill the page with as many copies as needed to fill the whole width of the host
                    const clonedContentElement = this.contentElement.nativeElement.cloneNode(true);
                    clonedContentElement.setAttribute('aria-hidden', 'true');
                    this.clonedElements.push(clonedContentElement);
                    this.pageElement.nativeElement.appendChild(clonedContentElement);
                }
            }
        }
        this.pageWidth = this.pageElement.nativeElement.clientWidth;
        // Duplicate the page
        const clonedPageElement = this.pageElement.nativeElement.cloneNode(true);
        clonedPageElement.setAttribute('aria-hidden', 'true');
        this.clonedElements.push(clonedPageElement);
        this.wrapperElement.nativeElement.appendChild(clonedPageElement);
        this.animationSpeedMultiplier = (this.pageWidth / 100) / this.speed;
        this.wrapperElement.nativeElement.style.animationDuration = `${this.animationSpeedMultiplier}s`;
        this.wrapperElement.nativeElement.style.animationDelay = `${-this.animationSpeedMultiplier}s`;
    }
    browserSetup() {
        if (isPlatformServer(this.platformId)) {
            return;
        }
        this.resizeObserver = new ResizeObserver(this.onContentResize.bind(this));
        this.resizeObserver.observe(this.contentElement.nativeElement);
        this.onContentResize();
        fromEvent(window, 'resize').pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.onContentResize();
        });
    }
    onPointerDown(event) {
        this.startMouseX = event.clientX;
        this.startAnimationDelay = parseFloat(this.wrapperElement.nativeElement.style.animationDelay) + this.animationSpeedMultiplier;
        this.document.addEventListener('pointermove', this.boundPointerMove);
        this.document.addEventListener('pointerup', () => {
            this.document.removeEventListener('pointermove', this.boundPointerMove);
        }, { once: true });
    }
    onPointerMove(event) {
        const percentLeft = ((event.clientX - this.startMouseX) / this.pageWidth);
        const newAnimationDelay = (percentLeft * this.animationSpeedMultiplier) - this.animationSpeedMultiplier + this.startAnimationDelay;
        if (newAnimationDelay > 0) {
            this.startAnimationDelay -= this.animationSpeedMultiplier;
        }
        this.wrapperElement.nativeElement.style.animationDelay = `${newAnimationDelay}s`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: MarqueeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: MarqueeComponent, isStandalone: true, selector: "kb-marquee", inputs: { speed: "speed", fill: "fill", enlarge: "enlarge" }, viewQueries: [{ propertyName: "contentElement", first: true, predicate: ["contentElement"], descendants: true, static: true }, { propertyName: "pageElement", first: true, predicate: ["pageElement"], descendants: true, static: true }, { propertyName: "wrapperElement", first: true, predicate: ["wrapperElement"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"wrapper\" #wrapperElement (pointerdown)=\"onPointerDown($event)\">\n  <div class=\"marquee-page marquee-item\" #pageElement>\n    <div class=\"marquee-content marquee-item\" #contentElement>\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;overflow:hidden;-webkit-user-select:none;user-select:none;cursor:grab}:host .wrapper{animation-name:marquee;animation-timing-function:linear;animation-iteration-count:infinite;display:flex}:host .wrapper:hover{animation-play-state:paused}@keyframes marquee{to{transform:translate(-50%)}}:host .marquee-page{flex-shrink:0;box-sizing:border-box;display:flex}:host .marquee-content{flex-shrink:0;display:flex}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: MarqueeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'kb-marquee', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [], template: "<div class=\"wrapper\" #wrapperElement (pointerdown)=\"onPointerDown($event)\">\n  <div class=\"marquee-page marquee-item\" #pageElement>\n    <div class=\"marquee-content marquee-item\" #contentElement>\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:flex;overflow:hidden;-webkit-user-select:none;user-select:none;cursor:grab}:host .wrapper{animation-name:marquee;animation-timing-function:linear;animation-iteration-count:infinite;display:flex}:host .wrapper:hover{animation-play-state:paused}@keyframes marquee{to{transform:translate(-50%)}}:host .marquee-page{flex-shrink:0;box-sizing:border-box;display:flex}:host .marquee-content{flex-shrink:0;display:flex}\n"] }]
        }], propDecorators: { speed: [{
                type: Input
            }], fill: [{
                type: Input
            }], enlarge: [{
                type: Input
            }], contentElement: [{
                type: ViewChild,
                args: ['contentElement', { static: true }]
            }], pageElement: [{
                type: ViewChild,
                args: ['pageElement', { static: true }]
            }], wrapperElement: [{
                type: ViewChild,
                args: ['wrapperElement', { static: true }]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { MarqueeComponent };
